import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { cn } from "@/shared/lib/utils"
import { formatNumber } from "@/shared/lib/staffing-shared"

import type { DailySummaryData } from "../types/daily-summary"

const GRADIENT_IMAGE_URL =
  "https://cdn.prod.website-files.com/689af392aa8c5cd6612c9ef7/689af392aa8c5cd6612c9f3f_gradient-map-2.webp"

// --- Types ---

export type WeeklyCallDistributionProps = {
  data: DailySummaryData | undefined
  totalWeeklyCalls: number
  monthlyContacts: number
  weekStart: string
}

type DayDistributionRow = {
  dayKey: string
  shortLabelKey: string
  calls: number
  isWeekend: boolean
}

// --- Constants ---

const WEEKEND_DAYS = new Set([
  "form.fields.distribution.saturday",
  "form.fields.distribution.sunday",
])

const SHORT_DAY_LABELS: Record<string, string> = {
  "form.fields.distribution.monday": "weeklyCallDistribution.days.mon",
  "form.fields.distribution.tuesday": "weeklyCallDistribution.days.tue",
  "form.fields.distribution.wednesday": "weeklyCallDistribution.days.wed",
  "form.fields.distribution.thursday": "weeklyCallDistribution.days.thu",
  "form.fields.distribution.friday": "weeklyCallDistribution.days.fri",
  "form.fields.distribution.saturday": "weeklyCallDistribution.days.sat",
  "form.fields.distribution.sunday": "weeklyCallDistribution.days.sun",
}

// --- Helpers ---

function transformToDistributionRows(data: DailySummaryData): DayDistributionRow[] {
  return data.rows.map((row) => ({
    dayKey: row.dayKey,
    shortLabelKey: SHORT_DAY_LABELS[row.dayKey] || row.dayKey,
    calls: row.calls,
    isWeekend: WEEKEND_DAYS.has(row.dayKey),
  }))
}

// --- Skeleton ---

export function WeeklyCallDistributionSkeleton() {
  return (
    <div className="w-full rounded-xl bg-[var(--color-surface-muted)]/60 p-5 animate-pulse">
      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 items-stretch">
        {/* Month skeleton */}
        <div className="flex-1 flex flex-col">
          <div className="mb-4">
            <div className="h-5 w-16 bg-[var(--color-brand-300)]/60 rounded mb-1" />
            <div className="h-3 w-28 bg-[var(--color-brand-300)]/40 rounded" />
          </div>
          <div className="flex-1 flex items-center">
            <div className="w-full">
              <div className="grid grid-cols-7 gap-1 mb-2">
                {Array.from({ length: 7 }).map((_, i) => (
                  <div key={i} className="h-3 bg-[var(--color-brand-300)]/30 rounded" />
                ))}
              </div>
              {Array.from({ length: 5 }).map((_, weekIdx) => (
                <div key={weekIdx} className="grid grid-cols-7 gap-1 mb-1">
                  {Array.from({ length: 7 }).map((_, dayIdx) => (
                    <div key={dayIdx} className="aspect-square rounded-lg bg-[var(--color-brand-300)]/40" />
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div className="border-t border-[var(--color-border)] pt-3 mt-4">
            <div className="h-6 w-24 bg-[var(--color-brand-300)]/50 rounded mb-1" />
            <div className="h-3 w-20 bg-[var(--color-brand-300)]/30 rounded" />
          </div>
        </div>

        {/* Divider skeleton */}
        <div className="hidden lg:flex items-center justify-center">
          <div className="space-y-1">
            <div className="w-6 h-0.5 bg-[var(--color-brand-300)]/40" />
            <div className="w-6 h-0.5 bg-[var(--color-brand-300)]/40" />
          </div>
        </div>

        {/* Week skeleton */}
        <div className="flex-1 flex flex-col">
          <div className="mb-4">
            <div className="h-5 w-14 bg-[var(--color-brand-300)]/60 rounded mb-1" />
            <div className="h-3 w-24 bg-[var(--color-brand-300)]/40 rounded" />
          </div>
          <div className="flex-1 flex items-center">
            <div className="w-full">
              <div className="grid grid-cols-7 gap-2 mb-2">
                {Array.from({ length: 7 }).map((_, i) => (
                  <div key={i} className="h-3 bg-[var(--color-brand-300)]/30 rounded" />
                ))}
              </div>
              <div className="grid grid-cols-7 gap-2 mb-2">
                {Array.from({ length: 7 }).map((_, i) => (
                  <div key={i} className="aspect-square rounded-lg bg-[var(--color-brand-300)]/40" />
                ))}
              </div>
              <div className="grid grid-cols-7 gap-2">
                {Array.from({ length: 7 }).map((_, i) => (
                  <div key={i} className="h-4 bg-[var(--color-brand-300)]/30 rounded" />
                ))}
              </div>
            </div>
          </div>
          <div className="border-t border-[var(--color-border)] pt-3 mt-4">
            <div className="h-6 w-20 bg-[var(--color-brand-300)]/50 rounded mb-1" />
            <div className="h-3 w-20 bg-[var(--color-brand-300)]/30 rounded" />
          </div>
        </div>
      </div>
    </div>
  )
}

// --- Main Component ---

export function WeeklyCallDistribution({
  data,
  totalWeeklyCalls,
  monthlyContacts,
}: WeeklyCallDistributionProps) {
  const { t } = useTranslation()

  const distributionRows = useMemo(() => {
    if (!data?.rows?.length) return []
    return transformToDistributionRows(data)
  }, [data])

  if (!data?.rows?.length) {
    return <WeeklyCallDistributionSkeleton />
  }

  const dayHeaders = ["S", "M", "T", "W", "T", "F", "S"]

  return (
    <div className="w-full rounded-xl bg-[var(--color-surface-muted)]/60 p-5">
      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 items-stretch">

        {/* LEFT: Month View */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="mb-4">
            <h4 className="body-base font-semibold text-[var(--color-text-primary)]">
              {t("weeklyCallDistribution.month")}
            </h4>
            <p className="body-sm text-[var(--color-text-secondary)]">
              {t("weeklyCallDistribution.monthSubtitle")}
            </p>
          </div>

          {/* Calendar Heatmap */}
          <div className="w-full flex-1 pb-4">
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-1">
              {dayHeaders.map((day, i) => (
                <div
                  key={i}
                  className="body-xs font-medium text-center text-[var(--color-text-muted)]"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar grid - 4 weeks */}
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: 4 * 7 }).map((_, i) => {
                const dayIndex = i % 7
                const weekIndex = Math.floor(i / 7)
                const isWeekend = dayIndex === 0 || dayIndex === 6
                // Calculate position as percentage of entire grid (7 cols x 4 rows)
                const xPos = (dayIndex / 6) * 100
                const yPos = (weekIndex / 3) * 100
                return (
                  <div
                    key={i}
                    className="aspect-square rounded-md overflow-visible"
                    style={!isWeekend ? {
                      background: `linear-gradient(to right, rgba(255, 131, 8, 0.8), rgba(255, 80, 67, 0.8) 50%, rgba(57, 43, 213, 0.8)), url(${GRADIENT_IMAGE_URL})`,
                      backgroundSize: "700% 400%, 700% 400%",
                      backgroundPosition: `${xPos}% ${yPos}%, ${xPos}% ${yPos}%`,
                      backgroundRepeat: "no-repeat",
                    } : {
                      backgroundColor: "var(--color-brand-200)",
                    }}
                  />
                )
              })}
            </div>
          </div>

          {/* Month Total */}
          <div className="border-t border-[var(--color-border)] pt-3 mt-auto">
            <div className="heading-sm text-[var(--color-text-primary)]">
              {formatNumber(monthlyContacts)} {t("weeklyCallDistribution.calls")}
            </div>
            <div className="body-sm text-[var(--color-text-secondary)]">
              {t("weeklyCallDistribution.totalThisMonth")}
            </div>
          </div>
        </div>

        {/* CENTER: Equals Divider */}
        <div className="hidden lg:flex items-center justify-center px-4">
          <div className="flex flex-col gap-1.5">
            <div className="w-8 h-1 rounded-full bg-[var(--color-border-strong)]" />
            <div className="w-8 h-1 rounded-full bg-[var(--color-border-strong)]" />
          </div>
        </div>

        {/* Mobile Divider */}
        <div className="lg:hidden flex items-center justify-center py-2">
          <div className="flex flex-col gap-1.5">
            <div className="w-8 h-1 rounded-full bg-[var(--color-border-strong)]" />
            <div className="w-8 h-1 rounded-full bg-[var(--color-border-strong)]" />
          </div>
        </div>

        {/* RIGHT: Week View */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="mb-4">
            <h4 className="body-base font-semibold text-[var(--color-text-primary)]">
              {t("weeklyCallDistribution.week")}
            </h4>
            <p className="body-sm text-[var(--color-text-secondary)]">
              {t("weeklyCallDistribution.weekSubtitle")}
            </p>
          </div>

          {/* Weekly Strip */}
          <div className="w-full flex-1 flex flex-col justify-center">
            {/* Day Labels */}
            <div className="grid grid-cols-7 gap-2 mb-1">
              {distributionRows.map((day) => (
                <div
                  key={day.dayKey}
                  className="body-xs font-medium text-center text-[var(--color-text-muted)]"
                >
                  {t(day.shortLabelKey)}
                </div>
              ))}
            </div>

            {/* Day Tiles */}
            <div className="grid grid-cols-7 gap-2 mb-1">
              {distributionRows.map((day, index) => {
                // Calculate position as percentage of entire row (7 cols)
                const xPos = (index / 6) * 100
                return (
                  <div
                    key={day.dayKey}
                    className="aspect-square rounded-md overflow-visible"
                    style={!day.isWeekend ? {
                      background: `linear-gradient(to right, rgba(255, 131, 8, 0.8), rgba(255, 80, 67, 0.8) 50%, rgba(57, 43, 213, 0.8)), url(${GRADIENT_IMAGE_URL})`,
                      backgroundSize: "700% auto, 700% auto",
                      backgroundPosition: `${xPos}% 50%, ${xPos}% 50%`,
                      backgroundRepeat: "no-repeat",
                    } : {
                      backgroundColor: "var(--color-brand-200)",
                    }}
                  />
                )
              })}
            </div>

            {/* Call Counts */}
            <div className="grid grid-cols-7 gap-2">
              {distributionRows.map((day) => (
                <div
                  key={day.dayKey}
                  className={cn(
                    "body-sm font-semibold text-center",
                    day.isWeekend || day.calls === 0
                      ? "text-[var(--color-text-muted)]"
                      : "text-[var(--color-text-primary)]"
                  )}
                >
                  {formatNumber(day.calls)}
                </div>
              ))}
            </div>
          </div>

          {/* Week Total */}
          <div className="border-t border-[var(--color-border)] pt-3 mt-auto">
            <div className="heading-sm text-[var(--color-text-primary)]">
              {formatNumber(totalWeeklyCalls)} {t("weeklyCallDistribution.calls")}
            </div>
            <div className="body-sm text-[var(--color-text-secondary)]">
              {t("weeklyCallDistribution.totalThisWeek")}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
